import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { format } from 'date-fns';
import { formatNumber } from 'libphonenumber-js';
import { get, omit, pick, keys, isEqual } from 'lodash';
import { Icon } from 'semantic-ui-react';

import { Modal, Button } from '../../../../components';
import { ClientForm } from '../form';
import useUserLookup from '../../../../lib/useUserLookup';
import { GET_CLIENTS_QUERY } from '../ClientsList';
import { CLIENT_SUMMARY_QUERY } from '../ClientsPage';
import { GET_ALL_CLIENTS_QUERY } from '../../../appointments/containers/form/ClientSelect';

export const UPDATE_CLIENT_QUERY = gql`
    mutation updateClient($input: UpdateClientInput!) {
        updateClient(input: $input) {
            client {
                id
                email
                firstName
                lastName
                phone
                notes
                birthday
                gender
                type
                blockedAt
                blockedBy {
                    id
                    firstName
                    lastName
                }
            }
        }
    }
`;

const intlMessages = defineMessages({
    existingsError: {
        id: 'pages.clients.form.ClientForm.existingsError',
        defaultMessage: 'This client does not exists',
    },
    phoneExistingsError: {
        id: 'pages.clients.form.ClientForm.phoneExistingsError',
        defaultMessage: 'Client with the same phone number already exists',
    },
    emailExistingsError: {
        id: 'pages.clients.form.ClientForm.emailExistingsError',
        defaultMessage: 'Client with the same email already exists',
    },
    emailPhoneError: {
        id: 'pages.clients.form.ClientForm.emailPhoneError',
        defaultMessage: 'There are two different users with the provided phone and email',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

const formatPhone = phone => (phone ? formatNumber(phone, 'International') : '+371');

function buildInitialValues(client) {
    return {
        firstName: client.firstName || '',
        lastName: client.lastName || '',
        phone: formatPhone(client.phone),
        email: client.email || '',
        notes: client.notes || '',
        birthday: client.birthday ? format(new Date(client.birthday), 'MM.dd.yyyy') : '',
        gender: client.gender || '',
        blocked: !!client.blockedAt,
    };
}

function PersonalInformation({ client, open, onClose, onClientUpdate, intl }) {
    const salon = useSelector(({ user }) => user.get('salon'));
    const userId = useSelector(({ user }) => user.get('id'));
    const searchQuery = useSelector(({ clients }) => clients.get('searchQuery') || '');
    const { userLookupStatus, userLookupUser, onUserLookup, resetUserLookup } = useUserLookup();

    const methods = useForm({ defaultValues: buildInitialValues(client) });
    const { handleSubmit, reset, formState: { isSubmitting, isDirty } } = methods;

    // Re-initialize form when client changes or dialog opens
    useEffect(() => {
        if (open) {
            reset(buildInitialValues(client));
        }
    }, [client.id, open]); // eslint-disable-line react-hooks/exhaustive-deps

    const [updateClient] = useMutation(UPDATE_CLIENT_QUERY);

    const readOnly =
        userLookupStatus === 'EXISTS' ||
        (client.type === 'GLOBAL' && !isDirty);

    const onSubmit = async formData => {
        const birthday = formData.birthday
            ? format(new Date(formData.birthday), 'MM.dd.yyyy')
            : null;
        const phone = (formData.phone || '').replace(/[^+\d]/g, '');
        const blockedAt = formData.blocked
            ? client.blockedAt || format(new Date(), 'yyyy-MM-dd HH:mm')
            : null;
        const blockedBy = formData.blocked
            ? (client.blockedBy && client.blockedBy.id) || userId
            : null;
        const { blocked, ...rest } = formData;

        try {
            const response = await updateClient({
                variables: {
                    input: { salonId: salon, clientId: client.id, ...rest, phone, birthday, blockedAt, blockedBy },
                },
                optimisticResponse: {
                    __typename: 'Mutation',
                    updateClient: {
                        __typename: 'UpdateClientPayload',
                        client: {
                            __typename: 'Client',
                            id: client.id,
                            type: client.type,
                            ...omit(formData, ['phone']),
                            phone,
                            birthday,
                            blockedAt: client.blockedAt,
                            blockedBy: client.blockedBy,
                        },
                    },
                },
                update: (store, res) => {
                    const updated = get(res, 'data.updateClient.client');
                    if (!updated) return;

                    try {
                        const variables = {
                            salon,
                            client: '',
                            withClient: false,
                        };
                        const data = store.readQuery({ query: GET_ALL_CLIENTS_QUERY, variables });
                        const nodes = data.viewer.salon.clients.nodes || [];
                        const nextNodes = nodes.some(o => o.id === updated.id)
                            ? nodes
                            : [{ ...updated, deletedAt: null }, ...nodes];
                        store.writeQuery({
                            query: GET_ALL_CLIENTS_QUERY,
                            variables,
                            data: {
                                ...data,
                                viewer: {
                                    ...data.viewer,
                                    salon: { ...data.viewer.salon, clients: { ...data.viewer.salon.clients, nodes: nextNodes } },
                                },
                            },
                        });
                    } catch (e) {
                        // cache not populated — safe to ignore
                    }

                    if (updated.id !== client.id) {
                        try {
                            const variables = { salon, search: searchQuery || undefined, first: 20 };
                            const data = store.readQuery({ query: GET_CLIENTS_QUERY, variables });
                            const edges = data.viewer.salon.clients.edges || [];
                            store.writeQuery({
                                query: GET_CLIENTS_QUERY,
                                variables,
                                data: {
                                    ...data,
                                    viewer: {
                                        ...data.viewer,
                                        salon: {
                                            ...data.viewer.salon,
                                            clients: {
                                                ...data.viewer.salon.clients,
                                                edges: edges.map(edge =>
                                                    edge.node.id === client.id ? { ...edge, node: updated } : edge,
                                                ),
                                            },
                                        },
                                    },
                                },
                            });
                        } catch (e) {
                            // safe to ignore
                        }
                    }

                    if (updated.gender !== client.gender) {
                        try {
                            const data = store.readQuery({ query: CLIENT_SUMMARY_QUERY, variables: { salon } });
                            const summary = get(data, 'viewer.salon.clients.summary') || {};
                            let nextMales = summary.males;
                            let nextFemales = summary.females;
                            if (updated.gender === 'MALE') { nextMales++; nextFemales--; }
                            else if (updated.gender === 'FEMALE') { nextFemales++; nextMales--; }
                            store.writeQuery({
                                query: CLIENT_SUMMARY_QUERY,
                                variables: { salon },
                                data: {
                                    ...data,
                                    viewer: {
                                        ...data.viewer,
                                        salon: {
                                            ...data.viewer.salon,
                                            clients: {
                                                ...data.viewer.salon.clients,
                                                summary: { ...summary, males: nextMales, females: nextFemales },
                                            },
                                        },
                                    },
                                },
                            });
                        } catch (e) {
                            // safe to ignore
                        }
                    }
                },
            });

            if (onClientUpdate) onClientUpdate(get(response, 'data.updateClient.client'));
            onClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                if (graphQLError.name === 'NotExists' && graphQLError.data?.error === 'ID_DOES_NOT_EXISTS') {
                    methods.setError('root', { message: intl.formatMessage(intlMessages.existingsError) });
                } else if (graphQLError.name === 'AlreadyExists') {
                    if (graphQLError.data?.error === 'PHONE_ALREADY_EXISTS') {
                        methods.setError('phone', { message: intl.formatMessage(intlMessages.phoneExistingsError) });
                    } else if (graphQLError.data?.error === 'EMAIL_ALREADY_EXISTS') {
                        methods.setError('phone', { message: intl.formatMessage(intlMessages.emailExistingsError) });
                    }
                } else if (graphQLError.name === 'Forbidden' && graphQLError.data?.error === 'THERE_ARE_TWO_DIFFERENT_USER_WITH_PROVIDED_PHONE_AND_EMAIL') {
                    methods.setError('root', { message: intl.formatMessage(intlMessages.emailPhoneError) });
                } else {
                    methods.setError('root', { message: graphQLError.message });
                }
                return;
            }
            methods.setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
        }
    };

    return (
        <FormProvider {...methods}>
            <Modal.Context>
                <Modal.Content>
                    <ClientForm
                        readOnly={readOnly}
                        userLookupStatus={userLookupStatus}
                        userLookupUser={userLookupUser}
                        onUserLookup={onUserLookup}
                        resetUserLookup={resetUserLookup}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || !isDirty}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.clients.details.editClient.save"
                            defaultMessage="Save"
                        />
                    </Button>
                    <Button onClick={onClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.clients.details.editClient.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal.Context>
        </FormProvider>
    );
}

export default injectIntl(PersonalInformation);
